import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useWxCopilotContext } from '@/components/pages/wx-copilot-host/context';
import { queryKeys } from '@/query-keys';

/** Collapse separators so camelCase and snake_case tool ids match the same fragments. */
function normalizeToolNameForQueryMatch(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function toolCallNameMatchesQueryFragments(
  toolName: string | null,
  fragments: readonly string[],
): boolean {
  if (!toolName) return false;
  const t = normalizeToolNameForQueryMatch(toolName);
  return fragments.some(fragment =>
    t.includes(normalizeToolNameForQueryMatch(fragment)),
  );
}

type QueryInvalidationRule = {
  toolNameFragments: readonly string[];
  queryKeysToInvalidate: readonly QueryKey[];
};

const QUERY_INVALIDATION_RULES: readonly QueryInvalidationRule[] = [
  {
    toolNameFragments: [
      'postInternalApiSourcingSourcingPurchasesBulkCreate',
      'putInternalApiSourcingSourcingPurchasesSourcingPurchaseId',
      'patchInternalApiSourcingSourcingPurchasesSourcingPurchaseIdUpdateCampaignStatus',
    ],
    queryKeysToInvalidate: [
      queryKeys.openingDetails.channels.list,
      queryKeys.openingDetails.detail,
      queryKeys.dashboard.openings,
    ],
  },
  {
    toolNameFragments: [
      'postToolsFunnels',
      'postInternalApiSourcingOpeningsOpeningSlug',
      'patchInternalApiSourcingDashboardOpeningSlug',
    ],
    queryKeysToInvalidate: [queryKeys.openingDetails.detail],
  },
  {
    toolNameFragments: [
      'patchInternalApiSourcingOpeningsOpeningSlugMapSourcingStages',
    ],
    queryKeysToInvalidate: [queryKeys.openingDetails.funnel.details],
  },
  {
    toolNameFragments: [
      'patchInternalApiSourcingDashboardOpeningSlug',
      'postToolsFunnels',
    ],
    queryKeysToInvalidate: [
      queryKeys.dashboard.openings,
      queryKeys.dashboard.analytics,
    ],
  },
  {
    toolNameFragments: ['patchInternalApiSourcingSourcingSettings'],
    queryKeysToInvalidate: [
      queryKeys.settings.automations,
      queryKeys.openingDetails.detail,
    ],
  },
  {
    toolNameFragments: [
      'deleteInternalApiSourcingSourcingChannels',
      'postInternalApiSourcingSourcingChannelsEnableChannels',
    ],
    queryKeysToInvalidate: [
      queryKeys.settings.channels.integrated,
      queryKeys.settings.channels.utmMapping,
    ],
  },
  {
    toolNameFragments: ['postInternalApiSourcingSourcingChannels'],
    queryKeysToInvalidate: [queryKeys.settings.channels.nonIntegrated],
  },
];

/**
 * Invalidates mapped query keys for a recorded copilot tool.
 * Subscribed from App so query-driven refetch works across routes.
 */
export function useCopilotQueryInvalidation(): void {
  const { lastToolCallName, clearLastToolCallName } = useWxCopilotContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    const keysToInvalidate = QUERY_INVALIDATION_RULES.flatMap(rule =>
      toolCallNameMatchesQueryFragments(
        lastToolCallName,
        rule.toolNameFragments,
      )
        ? rule.queryKeysToInvalidate
        : [],
    );

    if (keysToInvalidate.length === 0) {
      return;
    }

    void Promise.all(
      keysToInvalidate.map(queryKey =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    ).finally(() => {
      clearLastToolCallName();
    });
  }, [lastToolCallName, queryClient, clearLastToolCallName]);
}
